import { Component, OnInit } from '@angular/core';
import { ListService, LocalizationService, PagedResultDto } from '@abp/ng.core';
import { Column } from '../shared/components/metro-table/metro-table.component';
import { GetOrderDto, OrderDto } from '../proxy/orders/models';
import { OrderService } from '@proxy/orders';
import { OrderStatus } from '../proxy/enums/order-status.enum';
import { Router } from '@angular/router';
import { APP_ICONS } from '../shared/icons/app-icons';
import { finalize, take } from 'rxjs';
import { canConfirmPayment } from './order-payment';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';

interface FormattedOrderDto extends Omit<OrderDto, 'creationTime' | 'total' | 'orderStatus'> {
  creationTime: string;
  total: string;
  orderStatus: string;
  // winepassport#389 — se calcula antes de formatear orderStatus (la regla usa el enum).
  canConfirmPayment: boolean;
}

@Component({
  selector: 'app-backoffice-orders',
  templateUrl: './backoffice-orders.component.html',
  providers: [ListService],
  standalone: false,
})
export class BackofficeOrdersComponent implements OnInit {
  columns: Column[] = [
    // winepassport#385 — "Número"/"Estado" wrappeaban en el header con 10%.
    { name: 'id', label: '::Number', width: '13%', textColor: 'text-metro-blue-secondary', noWrap: true },
    { name: 'creationTime', label: '::Date', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'sommelierName', label: '::Sommelier', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'total', label: '::Total', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'orderStatus', label: '::Status', width: '13%', textColor: 'text-metro-blue-secondary', type: 'status' },
  ];

  actions: any[] = [
    { name: 'view', label: '::View', handler: (row: FormattedOrderDto) => this.viewOrder(row) },
    // winepassport#389 — confirmar pago sin tener que entrar al detalle.
    {
      name: 'confirmPayment',
      label: '::ConfirmPayment',
      handler: (row: FormattedOrderDto) => this.showConfirmPaymentModal(row),
      condition: (row: FormattedOrderDto) => row.canConfirmPayment && !this.confirmingOrderIds.has(row.id),
    },
  ];

  data: PagedResultDto<FormattedOrderDto> = { items: [], totalCount: 0 };
  // winepassport#389 — órdenes con una confirmación de pago en curso: la acción se oculta /
  // deshabilita hasta que vuelve la respuesta, para no mandar la misma confirmación dos veces.
  confirmingOrderIds = new Set<number>();
  searchOrder = {} as GetOrderDto;
  showDeliveryConfirmModal = false;
  orderToDeliver: FormattedOrderDto | null = null;
  isLoading = true;
  isExporting = false;
  excelIcon = APP_ICONS.excel;

  constructor(
    public readonly list: ListService,
    private orderService: OrderService,
    private router: Router,
    private confirmation: ConfirmationModalService,
    private localization: LocalizationService
  ) { }

  ngOnInit(): void {
    const streamCreator = (query) =>
      this.orderService.getList({ ...this.searchOrder, ...query });

    this.list.hookToQuery(streamCreator).subscribe((result) => {
      // Format the data before displaying it
      this.data = {
        ...result,
        items: result.items.map(item => this.formatOrderItem(item))
      };
      this.isLoading = false;
    });
  }

  private formatOrderItem(item: OrderDto): FormattedOrderDto {
    return {
      ...item,
      creationTime: this.formatDate(item.creationTime),
      total: this.formatCurrency(item.total),
      orderStatus: this.formatOrderStatus(item.orderStatus),
      canConfirmPayment: canConfirmPayment(item),
    };
  }

  private formatOrderStatus(status: OrderStatus | undefined): string {
    if (status === undefined) return '::Unknown';

    switch (status) {
      case OrderStatus.Pending:
        return 'OrderPending';
      case OrderStatus.Paid:
        return 'Paid';
      case OrderStatus.Shipped:
        return 'Shipped';
      case OrderStatus.Delivered:
        return 'Delivered';
      case OrderStatus.Cancelled:
        return 'Cancelled';
      case OrderStatus.PendingPayment:
        return 'OrderPendingPayment';
      default:
        return 'Unknown';
    }
  }

  onSearch(searchTerm: string) {
    this.isLoading = true;
    this.searchOrder.keyword = searchTerm;
    this.list.page = 0;
    this.list.get();
  }

  onClearSearch() {
    this.isLoading = true;
    this.searchOrder = {} as GetOrderDto;
    this.list.page = 0;
    this.list.get();
  }

  viewOrder(order: FormattedOrderDto) {
    // Navigate to the order detail page
    this.router.navigate(['/backoffice/orders', order.id]);
  }

  openCreate() {
    this.router.navigate(['/backoffice/orders/create']);
  }

  // winepassport#389 — modal reutilizable (max-w-md) en vez de app-metro-modal, que en
  // escritorio ocupaba casi todo el ancho.
  showConfirmPaymentModal(order: FormattedOrderDto) {
    if (this.confirmingOrderIds.has(order.id)) return;
    this.confirmation
      .confirm({
        title: '::ConfirmPayment',
        message: '::ConfirmPaymentMessage',
        type: 'warning',
        confirmText: this.localization.instant('::ConfirmPayment'),
        cancelText: this.localization.instant('::Cancel'),
        showCancelButton: true,
      })
      .subscribe(confirmed => {
        if (confirmed) this.onConfirmPayment(order);
      });
  }

  // winepassport#389 — la orden llega por parámetro, sin estado compartido en el componente:
  // con dos confirmaciones seguidas, la respuesta de la primera se aplicaba a la fila de la segunda.
  onConfirmPayment(order: FormattedOrderDto) {
    if (this.confirmingOrderIds.has(order.id)) return;
    this.confirmingOrderIds.add(order.id);
    // Los errores los muestra el manejador global de ABP (modal con el mensaje del backend).
    this.orderService.confirmBankPayment(order.id)
      .pipe(finalize(() => this.confirmingOrderIds.delete(order.id)))
      .subscribe({
      next: (updatedOrder) => {
        // Update the order in the list (formatOrderItem recalcula canConfirmPayment, así
        // la acción desaparece sola una vez confirmado el pago — winepassport#389).
        const index = this.data.items.findIndex(item => item.id === order.id);
        if (index !== -1) {
          this.data.items[index] = {
            ...this.data.items[index],
            ...this.formatOrderItem(updatedOrder),
          };
        }
      },
      error: (error) => {
        // Handle error
        console.error('Error confirming payment:', error);
      }
    });
  }

  showConfirmDeliveryModal(order: FormattedOrderDto) {
    this.orderToDeliver = order;
    this.showDeliveryConfirmModal = true;
  }

  onConfirmDelivery() {
    if (!this.orderToDeliver) return;

    // Call the service method to change order status to delivered
    this.orderService.changeOrderStatus(this.orderToDeliver.id, OrderStatus.Delivered).subscribe({
      next: (updatedOrder) => {
        // Update the order in the list
        const index = this.data.items.findIndex(item => item.id === this.orderToDeliver!.id);
        if (index !== -1) {
          this.data.items[index] = {
            ...this.data.items[index],
            ...updatedOrder,
            creationTime: this.formatDate(updatedOrder.creationTime),
            total: this.formatCurrency(updatedOrder.total),
            orderStatus: this.formatOrderStatus(updatedOrder.orderStatus)
          };
        }
        // Show success message
        // Close the modal
        this.showDeliveryConfirmModal = false;
        this.orderToDeliver = null;
      },
      error: (error) => {
        // Handle error
        console.error('Error delivering order:', error);
        // Close the modal
        this.showDeliveryConfirmModal = false;
        this.orderToDeliver = null;
      }
    });
  }

  confirmedPayment(order: FormattedOrderDto) {
    this.orderService.confirmBankPayment(order.id).subscribe({
      next: (updatedOrder) => {
        // Update the order in the list
        const index = this.data.items.findIndex(item => item.id === order.id);
        if (index !== -1) {
          this.data.items[index] = {
            ...this.data.items[index],
            ...updatedOrder,
            creationTime: this.formatDate(updatedOrder.creationTime),
            total: this.formatCurrency(updatedOrder.total),
            orderStatus: this.formatOrderStatus(updatedOrder.orderStatus)
          };
        }
        // Show success message
      },
      error: (error) => {
        // Handle error
        console.error('Error confirming payment:', error);
      }
    });
  }

  generateExcel() {
    this.isExporting = true;

    this.orderService.generateExcel({ responseType: 'blob' } as any)
      .pipe(
        take(1),
        finalize(() => this.isExporting = false)
      )
      .subscribe({
        next: (blob) => {
          const fileName = `Ordenes_${new Date().toISOString().slice(0, 10)}.xlsx`;
          const downloadUrl = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.click();

          window.URL.revokeObjectURL(downloadUrl);
        },
        error: (err) => {
          console.error('Error al exportar órdenes', err);
        }
      });
  }

  formatDate(dateValue: string | Date | undefined): string {
    if (!dateValue) return '';
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    return date.toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  formatCurrency(amount: number): string {
    if (amount === undefined || amount === null) return '$0,00';
    return '$' + amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // winepassport#389 — paginado de la vista de tarjetas (mobile). La tabla pagina sola.
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.data.totalCount / this.list.maxResultCount));
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.list.page = page - 1;  // Convert from 1-indexed (used by metro-table) to 0-indexed (used by ABP ListService)
    this.list.get();
  }
}
